// useDashboardStats — derived statistics for course dashboard.
//
// Extracted from course-dashboard.tsx which had ~16 lines of inline stat
// computation (due/flashcard count, wrong-answer counts, bookmark count,
// chapter/topic stats, cognitive distribution, weakest chapters/topics).
//
// Wrapping in useMemo avoids recomputing on every render when draft/pkg/progress
// are referentially stable (Zustand selectors return stable refs on no-change).
//
// Session 14 Step 5 · Tier 2 View Decomposition.

import { useMemo } from 'react';
import { getDueCards } from '../srs.js';
import { isUnresolved, type ComponentId } from '@examforge/shared/schemas';
import {
  chapterStats,
  topicStats,
  cognitiveDistribution,
  weakestChapters,
  weakestTopics,
  chapterCognitiveMatrix,
  type ChapterStats,
  type TopicStats,
  type CognitiveDistribution,
  type ChapterCognitiveCell,
} from '../stats.js';
import type { CourseDraft, CourseProgress, IngestPackage } from '@examforge/shared/schemas';

export interface DashboardStats {
  enabled: Set<ComponentId>;
  dueCount: number;
  unresolvedCount: number;
  bookmarkCount: number;
  flaggedCount: number;
  chStats: ChapterStats[];
  tpStats: TopicStats[];
  cogDist: CognitiveDistribution[];
  /** B5.3: per-chapter × per-cognitive-level wrong-answer matrix (heatmap cells). */
  cogMatrix: ChapterCognitiveCell[];
  weakChapters: Array<{ chapterId: string; chapterName: string; mastery: number | null }>;
  weakTopics: Array<{ topicId: string; topicName: string; unresolvedWrong: number }>;
  totalCogLabeled: number;
}

export function useDashboardStats(
  draft: CourseDraft,
  pkg: IngestPackage,
  progress: CourseProgress,
): DashboardStats {
  return useMemo(() => {
    const enabled = new Set<ComponentId>(draft.enabledComponents);
    const dueCount = getDueCards(pkg.flashcards, progress).length;
    const unresolvedCount = Object.values(progress.wrongAnswers).filter(isUnresolved).length;
    const bookmarkCount = Object.keys(progress.bookmarks).length;
    const flaggedCount = Object.values(progress.itemFeedback ?? {}).filter((f) => f.flagged).length;

    const chStats = chapterStats(pkg, progress);
    const tpStats = topicStats(pkg, progress);
    const cogDist = cognitiveDistribution(pkg);
    const cogMatrix = chapterCognitiveMatrix(pkg, progress);
    const weakChaptersResult = weakestChapters(chStats);
    const weakTopicsResult = weakestTopics(tpStats);

    const totalCogLabeled = cogDist.reduce((s, b) => s + (b.level === 'unlabeled' ? 0 : b.count), 0);

    return {
      enabled,
      dueCount,
      unresolvedCount,
      bookmarkCount,
      flaggedCount,
      chStats,
      tpStats,
      cogDist,
      cogMatrix,
      weakChapters: weakChaptersResult,
      weakTopics: weakTopicsResult,
      totalCogLabeled,
    };
  }, [draft.enabledComponents, pkg, progress]);
}
